import React = require("react");
import { MessageCard } from "../../../designSystem/Message";
import clone = require("clone");
import styled from "styled-components";
import { TextSaveField } from "../../../designSystem/formField/text/TextSaveField";
import { Button } from "../../../designSystem/button/Button";
import { TextField } from "../../../designSystem/formField/text/TextField";
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { Segmentation, Survey, LanguageCode, fieldValidation } from '../../../amorg-common';
import { Divider } from "../../../designSystem/Divider";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { deleteSurveySegmentation, moveSurveySegment, updateSurveySegmentation } from "../../../api/surveyClientApi";
import { surveyValidation } from "../../../amorg-common/modules/fieldValidation/fields/surveyFieldValidation";



type SegmentationEditorProps = {
    survey:Survey,
    selectedSegmentationIndex: number,
    selectedLanguage:LanguageCode,
    onSegmentationsUpdated: () => void,
    onDeleted: () => void,
    onClose: () => void
}

export function SegmentationEditor(props: SegmentationEditorProps): JSX.Element {
    const [error, setError] = React.useState('');
    const [segmentationValue, setSegmentationName] = React.useState('');

    function getSelectedSegmentation():Segmentation
    {
        const seg = props.survey.segmentations[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            return {name:seg.name, values:seg.values};
        
        const trans = seg.translations.find(t => t.languageCode===props.selectedLanguage);
        if(!trans) throw new Error('Missing translation');
        return { name:trans.name, values:trans.values };
    }

    async function handleSaveSegmentName(value:string) {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            ss.name = value;
        else
            ss.translations.find(t => t.languageCode===props.selectedLanguage).name = value;
        
        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Navnet på segmentet er ikke gyldigt. Teksten skal være kort, og unik blandt alle segmenter');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleSaveSegmentValue(index: number, value: string) 
    {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        if(props.selectedLanguage===props.survey.languageCode)
            ss.values[index] = value;
        else
            ss.translations.find(t => t.languageCode===props.selectedLanguage).values[index] = value;

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Fejl i segment');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleClickAddValue() {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        ss.values.push(segmentationValue);
        for(const t of ss.translations)
            t.values.push(segmentationValue);

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('Navnet på værdien er ikke gyldig. Teksten skal være kort, og unik blandt alle værdierne i segmenteringen');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            setSegmentationName('');
            props.onSegmentationsUpdated();
        }
    }

    async function handleDeleteValue(index:number) {
        const cloned = clone(props.survey.segmentations);
        const ss = cloned[props.selectedSegmentationIndex];
        ss.values.splice(index, 1);
        for(const t of ss.translations)
            t.values.splice(index, 1);

        if (surveyValidation.surveySegmentation.validateSurveySegmentationList(props.survey.translationLanguages, cloned) !== 'ok')
            setError('En segmentering skal indeholde mindst 2 værdier. Tilføj en ny værdi før du sletter de eksisterende.');
        else {
            setError('');
            await updateSurveySegmentation(props.survey.id, props.selectedSegmentationIndex, props.selectedSegmentationIndex, ss);
            props.onSegmentationsUpdated();
        }
    }

    async function handleDeleteSegmentation() {
        await deleteSurveySegmentation(props.survey.id, props.selectedSegmentationIndex);
        props.onDeleted();
    }

    async function handleMoveSegment(move: 'up' | 'down', valueIndex) 
    {
        const ss = getSelectedSegmentation();

        if (move === 'up') {
            if (valueIndex > 0) {

                await moveSurveySegment(props.survey.id, props.survey.segmentations, props.selectedSegmentationIndex, valueIndex, valueIndex - 1);
                props.onSegmentationsUpdated();
            }
        }
        if (move === 'down') {
            if (valueIndex < ss.values.length - 1) {
                await moveSurveySegment(props.survey.id, props.survey.segmentations, props.selectedSegmentationIndex, valueIndex, valueIndex + 1);
                props.onSegmentationsUpdated();
            }
        }
    }

    const segmentation = getSelectedSegmentation();

    return (
        <>
            <Flex flexDirection="column" gap={16}>
                
                {error &&
                    <MessageCard variant="error">
                        {error}
                    </MessageCard>
                }

                <TextSaveField
                    label="Navn"
                    value={segmentation.name}
                    onSave={(value, isValid)=>{ 
                        if(isValid)
                           handleSaveSegmentName(value);
                    }}
                    validator={(currentValue) => 
                        fieldValidation.survey.segmentationName(currentValue) 
                            ? {valid:true}
                            : {valid:false, errorMessage:`Ugyldig værdi. Skal udfyldes med maks. 50 (${currentValue.length}) tegn.`}
                    } />

                <Divider />

                <div>
                    {segmentation.values.map((value, index) =>

                        <SegmentWrapper key={index}>
                            <SegmentMove>
                                <div>
                                    <Button variant="text"
                                        icon="arrows_chevron_chevronUp"
                                        onClick={() => handleMoveSegment('up', index)}
                                        text={""} />
                                    <Button variant="text"
                                        icon="arrows_chevron_chevronDown"
                                        onClick={() => handleMoveSegment('down', index)}
                                        text={""} />
                                </div>
                            </SegmentMove>
                            <SegmentName>
                                <SegmentEditor
                                    index={index}
                                    value={value}
                                    onSave={handleSaveSegmentValue}
                                />
                            </SegmentName>
                            <SegmentDelete>
                                <Button variant="text"
                                    icon="actions_close"
                                    text={""}
                                    onClick={() => handleDeleteValue(index)} />
                            </SegmentDelete>
                        </SegmentWrapper>
                    )}
            
                    <SegmentWrapper>
                        <SegmentName>
                            <TextField
                                label="Ny valgmulighed"
                                value={segmentationValue}
                                onChange={val => setSegmentationName(val)} 
                                validator={() => { return { valid:true } } } />

                            <ButtonRow variant="right-aligned" marginTop={10}>
                                <Button variant="outlined"
                                    disabled={segmentationValue === ''}
                                    onClick={handleClickAddValue}
                                    icon="actions_plus"
                                    text={"Tilføj"} />
                            </ButtonRow>

                        </SegmentName>

                    </SegmentWrapper>
                </div>

                <ButtonRow variant="space-between">
                    <Button variant="destructive"
                        text={"Slet denne segmentering"}
                        onClick={handleDeleteSegmentation}
                        icon={"actions_close"} />

                    <Button variant="filled"
                        onClick={props.onClose}
                        text={"Luk"} />
                </ButtonRow>
            </Flex>
        </>
    );
}

type SegmentEditorProps = {
    index: number,
    value: string,
    onSave: (index: number, value: string) => void
}

function SegmentEditor(props: SegmentEditorProps): JSX.Element {

    return (
        <TextSaveField
            label={`Valgmulighed ${props.index + 1}`}
            value={props.value}
            onSave={(value) => props.onSave(props.index, value)} 
            validator={(currentValue) => 
                fieldValidation.survey.segmentationValue(currentValue)
                    ? {valid:true}
                    : {valid:false, errorMessage:`Ugyldig værdi. Skal udfyldes med maks. 50 (${currentValue.length}) tegn.`}
                
            } />
    );
}

const SegmentWrapper = styled.div`
    display:grid;
    grid-template-columns: 40px auto 40px;
    grid-template-areas: "segmentMove segmentName segmentDelete";
`

const SegmentMove = styled.div`
    grid-area: segmentMove;
    align-content: flex-end;

    &:first-child{
        margin-top:30px;
    }

    & button{
        height: 20px;
    }
`

const SegmentName = styled.div`
    grid-area: segmentName;
`

const SegmentDelete = styled.div`
    grid-area: segmentDelete;
    padding-top:30px;
`